// Critical-fields contract — see aegle-docs critical_fields_contract.md
import { useCallback, useMemo } from "react";

import {
  ClaimantProjection,
  ClaimItemRowForecast,
  DocumentReviewProjection,
  ExtractedRecord,
  ReimbursementRowForecast,
  ReimbursementSummary,
} from "@/types";

import { createReimbursementSurvivorFilter } from "../components/document-review/reimbursementReviewHelpers";
import { absoluteIndexOf } from "../utils/recordIndexing";
import { getRecordData } from "../utils/recordUtils";

/**
 * Resolve extracted records to their projection-side data.
 *
 * The review-projection endpoint returns per-claimant + per-reimbursement
 * forecasts keyed by `extracted_record_index`. The grid renders extracted
 * records (sometimes deduplicated). Mapping between the two requires a small
 * cluster of memos and getters that all key off the same projection payload —
 * gathered here so the consuming component (DocumentReviewTab) stays a
 * coordinator instead of an orchestrator of lookups.
 *
 * Two lookup paths exist for reimbursement summaries:
 *   1. By extracted_record_index of the summary's anchor record. The backend
 *      points this at the first record that survives filtering — e.g. record
 *      8 for QBE's Lisa Martin where records 1-7 are per-line and dropped.
 *   2. By normalized claimant identity (existing claimant ID, lowercased
 *      name). Used as the fallback when the grid's deduped representative
 *      points at an earlier record (record 1) but the summary's anchor is
 *      later (record 8). AB#4455. A name shared by MORE THAN ONE summary is
 *      refused, not last-write-wins resolved: a name is not an identity
 *      (#5766, and #6830's evidence in this same PR — every name shared on
 *      the motivating file), and resolving a collision would hand a record
 *      another claimant's dollar figures. Ambiguous → no summary → no chip,
 *      the same fail-closed choice absoluteIndexOf makes.
 *
 * The survivor filter exposed here is the cross-surface predicate consolidated
 * in reimbursementReviewHelpers — see that module for the keepUnknown
 * semantics.
 */
export interface ReviewProjectionLookups {
  getProjectionForRecord: (record: ExtractedRecord) => ClaimantProjection | undefined;
  getSummaryForRecord: (record: ExtractedRecord) => ReimbursementSummary | undefined;
  getReimbursementRowForRecord: (record: ExtractedRecord) => ReimbursementRowForecast | undefined;
  /** Per-row claim_item forecast (New / On file) for the Claims tab + nested claim-item table. */
  getClaimItemRowForRecord: (record: ExtractedRecord) => ClaimItemRowForecast | undefined;
  /** Filter closure shared across the three reimbursement-survivor call sites. */
  filterReimbursementSurvivors: (records: ExtractedRecord[], options?: { keepUnknown?: boolean }) => ExtractedRecord[];
}

interface UseReviewProjectionLookupsArgs {
  /** Review projection payload (null while loading or on error). */
  projection: DocumentReviewProjection | null;
}

/**
 * Note the absent `records` argument. It used to be required, solely to build
 * the record→position map, and dropping it is the structural half of the
 * #6755 fix: this hook can no longer consult an array to decide what a
 * record's index is, because it no longer has one.
 */
export const useReviewProjectionLookups = ({ projection }: UseReviewProjectionLookupsArgs): ReviewProjectionLookups => {
  // Absolute index read off the record, not derived from its position here
  // (#6755). The previous `new Map(records.map((r, i) => [r, i]))` was correct
  // only because `records` was always the complete set; under a windowed fetch
  // page 2's positions restart at 0 and every join below would attach the
  // WRONG claimant's forecast, deductible and YTD figures — plausible numbers,
  // silently misassigned, with nothing on screen to give it away.
  //
  // Grid rows still resolve correctly through deduplication: the projection's
  // extracted_record_index can point at a duplicate the grid dropped, and the
  // stamped index survives that because it never depended on the rendered
  // array in the first place.

  const projectionByIndex = useMemo(() => {
    const map = new Map<number, ClaimantProjection>();
    if (!projection || !Array.isArray(projection.claimants)) return map;
    for (const cp of projection.claimants) {
      map.set(cp.extracted_record_index, cp);
    }
    return map;
  }, [projection]);

  const getProjectionForRecord = useCallback(
    (record: ExtractedRecord): ClaimantProjection | undefined => {
      const idx = absoluteIndexOf(record);
      if (idx === undefined) return undefined;
      return projectionByIndex.get(idx);
    },
    [projectionByIndex]
  );

  const summaryByIndex = useMemo(() => {
    const map = new Map<number, ReimbursementSummary>();
    if (!projection?.reimbursement_summaries) return map;
    for (const s of projection.reimbursement_summaries) {
      map.set(s.extracted_record_index, s);
    }
    return map;
  }, [projection]);

  const summaryByClaimantKey = useMemo(() => {
    const map = new Map<string, ReimbursementSummary>();
    if (!projection?.reimbursement_summaries) return map;

    // First pass counts names so the second pass can refuse collisions. A
    // lowercased name shared by two summaries must resolve to NEITHER —
    // last-write-wins would silently hand one claimant's record the other
    // claimant's reimbursement summary (dollar figures, not labels).
    const nameCounts = new Map<string, number>();
    for (const s of projection.reimbursement_summaries) {
      if (!s.claimant_name) continue;
      const key = s.claimant_name.trim().toLowerCase();
      nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
    }

    for (const s of projection.reimbursement_summaries) {
      if (s.existing_claimant_id) map.set(`id:${s.existing_claimant_id}`, s);
      if (s.claimant_name) {
        const key = s.claimant_name.trim().toLowerCase();
        if (nameCounts.get(key) === 1) map.set(`name:${key}`, s);
      }
    }
    return map;
  }, [projection]);

  const getSummaryForRecord = useCallback(
    (record: ExtractedRecord): ReimbursementSummary | undefined => {
      const idx = absoluteIndexOf(record);
      if (idx !== undefined) {
        const direct = summaryByIndex.get(idx);
        if (direct) return direct;
      }
      // Fallback: deduplicated claimant rows expose a representative whose
      // index doesn't match the summary's anchor (which points at the
      // surviving reimbursement row, not the first claimant occurrence).
      // Match by claimant identity instead. Guard typeof since
      // ExtractedRecord field values are typed as ValidAny — coercing a
      // non-string would silently call .trim on it.
      const data = getRecordData(record);
      const claimantId = typeof data.claimant_id_ref === "string" ? data.claimant_id_ref.trim() : "";
      if (claimantId) {
        const byId = summaryByClaimantKey.get(`id:${claimantId}`);
        if (byId) return byId;
      }
      const name = typeof data.claimant_name === "string" ? data.claimant_name.trim().toLowerCase() : "";
      if (name) return summaryByClaimantKey.get(`name:${name}`);
      return undefined;
    },
    [summaryByIndex, summaryByClaimantKey]
  );

  // Per-row reimbursement forecast — drives the row-level "On file" marker in
  // the nested reimbursements table. Without this, the parent claimant chip
  // says "Creates 1 · 1 unlinked" but the reviewer can't tell which of the N
  // expanded rows is the new one and which is already on file.
  const reimbursementRowByIndex = useMemo(() => {
    const map = new Map<number, ReimbursementRowForecast>();
    if (!projection?.reimbursement_summaries) return map;
    for (const summary of projection.reimbursement_summaries) {
      for (const row of summary.rows ?? []) {
        map.set(row.extracted_record_index, row);
      }
    }
    return map;
  }, [projection]);

  const getReimbursementRowForRecord = useCallback(
    (record: ExtractedRecord): ReimbursementRowForecast | undefined => {
      const idx = absoluteIndexOf(record);
      if (idx === undefined) return undefined;
      return reimbursementRowByIndex.get(idx);
    },
    [reimbursementRowByIndex]
  );

  // Per-row claim_item forecast — drives the "New" / "On file" marker on the
  // Claims tab and the nested claim-item table under an expanded claimant. Flat
  // array keyed by extracted_record_index (unlike reimbursements, claim_item
  // records are 1:1 with extracted records so there is no per-claimant nesting).
  const claimItemRowByIndex = useMemo(() => {
    const map = new Map<number, ClaimItemRowForecast>();
    for (const fc of projection?.claim_item_forecasts ?? []) {
      map.set(fc.extracted_record_index, fc);
    }
    return map;
  }, [projection]);

  const getClaimItemRowForRecord = useCallback(
    (record: ExtractedRecord): ClaimItemRowForecast | undefined => {
      const idx = absoluteIndexOf(record);
      if (idx === undefined) return undefined;
      return claimItemRowByIndex.get(idx);
    },
    [claimItemRowByIndex]
  );

  // Indices that will become inserted reimbursements on approve. Anonymous
  // summaries (empty claimant_name) are excluded — claimant resolution fails
  // at load time, so they don't actually load. The reimbursement views (flat
  // tab + claimant-expanded nested table) show ONLY records in this set so
  // reviewers see exactly what approve will create — no chip-vs-no-chip
  // inference required. AB#4455.
  const reimbursementSurvivorIndices = useMemo(() => {
    const set = new Set<number>();
    if (!projection?.reimbursement_summaries) return set;
    for (const s of projection.reimbursement_summaries) {
      if (!s.claimant_name) continue; // anonymous bucket — won't actually load
      for (const row of s.rows ?? []) set.add(row.extracted_record_index);
    }
    return set;
  }, [projection]);

  // One survivor filter, three call sites — flat tab grid, nested expand, and
  // expand-badge count. Centralizing prevents the predicate drift that
  // produced the badge-count-vs-expanded-table bug AB#4455 fixed (AB#4492).
  const filterReimbursementSurvivors = useMemo(
    () => createReimbursementSurvivorFilter(reimbursementSurvivorIndices),
    [reimbursementSurvivorIndices]
  );

  return {
    getProjectionForRecord,
    getSummaryForRecord,
    getReimbursementRowForRecord,
    getClaimItemRowForRecord,
    filterReimbursementSurvivors,
  };
};
